/**
 * Centralized Payment Row Validation Utility
 *
 * This utility provides a single source of truth for payment row validation
 * logic, eliminating duplication across useTableOperations, paymentDetails schema,
 * and Table component validation.
 */


import { PaymentRow } from '../components/container/forms/schemas/DubaiPaymentApproval/paymentDetails.schema';
import {
  validateAccountNumber,
  validateBicSwift,
  validateIban,
  validateSortCode,
} from '../components/container/forms/schemas/Utils/bankingValidation';

export interface PaymentValidationContext {
  location?: 'uk' | 'nonUk';
  existingGridData?: PaymentRow[];
  rowIndex?: number; // For update operations
}

export interface PaymentValidationResult {
  success: boolean;
  errors?: Record<string, string>;
  data?: PaymentRow;
  rowIndex?: number;
}

/**
 * Validates a payment row with comprehensive business rules
 */
export const validatePaymentRow = (
  formData: Record<string, unknown>,
  context: PaymentValidationContext = {}
): PaymentValidationResult => {
  const errors: Record<string, string> = {};

  // Determine the effective location
  const location = formData.location || context.location;

  // Get existing grid data for duplicate checking
  const existingGridData = context.existingGridData || [];

  // ============= Basic Required Field Validation =============

  if (!formData.payee || String(formData.payee).trim() === '') {
    errors.payee = 'Payee is required';
  }

  if (!formData.currency || String(formData.currency).trim() === '') {
    errors.currency = 'Currency is required';
  }

  if (
    !formData.amount ||
    isNaN(Number(formData.amount)) ||
    Number(formData.amount) <= 0
  ) {
    errors.amount = 'Amount must be a positive number';
  }

  if (!formData.purpose || String(formData.purpose).trim() === '') {
    errors.purpose = 'Purpose is required';
  }

  if (!formData.deductBankCharges || String(formData.deductBankCharges).trim() === '') {
    errors.deductBankCharges = 'Deduct bank charges is required';
  }

  if (!formData.reference || String(formData.reference).trim() === '') {
    errors.reference = 'Payment reference is required';
  }

  if (!formData.ledgerDesc || String(formData.ledgerDesc).trim() === '') {
    errors.ledgerDesc = 'Ledger description is required';
  }

  if (!formData.type || String(formData.type).trim() === '') {
    errors.type = 'Account type is required';
  }

  // ============= Currency Custom Validation =============
  if (
    formData.currency === 'other' &&
    (!formData.currencyCustom ||
      String(formData.currencyCustom).trim().length < 2)
  ) {
    errors.currencyCustom = 'Currency code/name must be at least 2 characters';
  }

  // ============= Purpose Custom Validation =============
  if (
    formData.purpose === 'other' &&
    (!formData.purposeCustom ||
      String(formData.purposeCustom).trim().length < 2)
  ) {
    errors.purposeCustom = 'Purpose description must be at least 2 characters';
  }

  // ============= Location-Based Banking Field Validation =============
  if (location === 'uk') {
    const accountError = validateAccountNumber(formData.accno, 'Account number');
    if (accountError) {
      errors.accno = accountError;
    }

    const sortCodeError = validateSortCode(formData.sortCode, 'Sort code');
    if (sortCodeError) {
      errors.sortCode = sortCodeError;
    }
  } else if (location === 'nonUk') {
    const ibanError = validateIban(formData.iban, 'IBAN');
    if (ibanError) {
      errors.iban = ibanError;
    }

    const swiftError = validateBicSwift(
      formData.swiftBicCode,
      'SWIFT/BIC code'
    );
    if (swiftError) {
      errors.swiftBicCode = swiftError;
    }

    if (!formData.bankName || String(formData.bankName).trim().length < 2) {
      errors.bankName = 'Bank name is required for international payments';
    }
  }

  // ============= Duplicate Reference Validation =============
  const newReference = String(formData.reference || '').trim();
  if (newReference) {
    const duplicateReference = existingGridData.find(
      (row: PaymentRow, index: number) => {
        // Skip the current row being updated (for update operations)
        if (context.rowIndex !== undefined && index === context.rowIndex) {
          return false;
        }

        // Skip empty rows (rows with no meaningful data)
        const hasData = row.payee || row.amount || row.purpose || row.reference;
        if (!hasData) return false;

        return String(row.reference || '').trim() === newReference;
      }
    );

    if (duplicateReference) {
      errors.reference = 'Payment reference must be unique';
    }
  }

  // ============= Duplicate Payment Validation (Optional Business Rule) =============
  const newPayee = String(formData.payee || '').trim();
  const newAmount = String(formData.amount || '').trim();
  if (newPayee && newAmount) {
    const duplicatePayment = existingGridData.find(
      (row: PaymentRow, index: number) => {
        // Skip the current row being updated (for update operations)
        if (context.rowIndex !== undefined && index === context.rowIndex) {
          return false;
        }

        // Skip empty rows
        const hasData = row.payee || row.amount || row.purpose || row.reference;
        if (!hasData) return false;

        return (
          String(row.payee || '').trim() === newPayee &&
          String(row.amount || '').trim() === newAmount
        );
      }
    );

    if (duplicatePayment) {
      errors.payee = 'Duplicate payment (same payee and amount) already exists';
    }
  }

  // ============= Return Validation Result =============
  if (Object.keys(errors).length > 0) {
    return {
      success: false,
      errors,
    };
  }

  return {
    success: true,
    data: formData as unknown as PaymentRow,
    rowIndex: context.rowIndex,
  };
};

/**
 * Validates a payment row for add operations
 */
export const validatePaymentRowForAdd = (
  formData: Record<string, unknown>,
  context: PaymentValidationContext = {}
): PaymentValidationResult => {
  return validatePaymentRow(formData, context);
};

/**
 * Validates a payment row for update operations
 */
export const validatePaymentRowForUpdate = (
  formData: Record<string, unknown>,
  rowIndex: number,
  context: PaymentValidationContext = {}
): PaymentValidationResult => {
  return validatePaymentRow(formData, {
    ...context,
    rowIndex,
  });
};

/**
 * Helper to extract existing grid data from various formats
 */
export const extractExistingGridData = (gridData: unknown): PaymentRow[] => {
  if (!gridData) return [];

  if (Array.isArray(gridData)) {
    return gridData as PaymentRow[];
  }

  if (typeof gridData === 'object' && gridData !== null && 'data' in gridData) {
    const dataValue = (gridData as Record<string, unknown>).data;
    if (Array.isArray(dataValue)) {
      return dataValue as PaymentRow[];
    }
  }

  return [];
};

/**
 * Helper to create validation context from form state
 */
export const createValidationContext = (
  formState: { data?: Record<string, unknown> },
  context?: Record<string, unknown>
): PaymentValidationContext => {
  const location =
    context?.location ||
    (formState.data?.location as 'uk' | 'nonUk' | undefined);

  const existingGridData = extractExistingGridData(
    context?.gridData || formState.data?.gridData
  );

  return {
    location: location as 'uk' | 'nonUk' | undefined,
    existingGridData,
  };
};
